"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { SolutionDraft } from "./use-solution-draft";
import type { WorkspaceSubmissionView } from "./workspace-result";
import { submissionCompileFeedback } from "./compile-diagnostics";

const fingerprintsSchema = z.record(z.string().regex(/^[a-f0-9]{64}$/));
type Fingerprints = z.infer<typeof fingerprintsSchema>;

/** Runtime is included so identical code in a different language never matches. */
export async function fingerprintDraft(draft: SolutionDraft): Promise<string | null> {
  try {
    const bytes = new TextEncoder().encode(JSON.stringify([draft.languageKey, draft.languageVersion, draft.sourceCode]));
    const hash = await window.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
  } catch {
    // Unavailable crypto or storage means message/navigation without markers.
    return null;
  }
}

export function useCompileFeedback(storageKey: string, draft: SolutionDraft | null, submissionId: string | null, view: WorkspaceSubmissionView | null) {
  const key = `${storageKey}:submission-fingerprints`;
  const [fingerprints, setFingerprints] = useState<{ key: string; values: Fingerprints }>({ key, values: {} });
  const [current, setCurrent] = useState<{ draft: SolutionDraft; hash: string | null } | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  useEffect(() => {
    let values: Fingerprints = {};
    try {
      const parsed = fingerprintsSchema.safeParse(JSON.parse(window.localStorage.getItem(key) ?? "{}"));
      if (parsed.success) values = parsed.data;
    } catch { /* Corrupt/unavailable storage carries no attribution. */ }
    setFingerprints({ key, values });
  }, [key]);
  useEffect(() => {
    let cancelled = false;
    if (draft) void fingerprintDraft(draft).then((hash) => {
      if (!cancelled) setCurrent({ draft, hash });
    });
    return () => { cancelled = true; };
  }, [draft]);
  const remember = useCallback(async (id: string, submitted: SolutionDraft) => {
    const hash = await fingerprintDraft(submitted);
    if (!hash) return;
    setFingerprints((previous) => {
      const values = Object.fromEntries(Object.entries({ ...(previous.key === key ? previous.values : {}), [id]: hash }).slice(-50));
      try { window.localStorage.setItem(key, JSON.stringify(values)); } catch { /* In-memory attribution still works. */ }
      return { key, values };
    });
  }, [key]);
  const clear = useCallback(() => setDismissed(submissionId), [submissionId]);
  const status = view?.status ?? "";
  const verdict = view?.verdict ?? null;
  const languageKey = view?.languageKey ?? null;
  const compileOutput = view?.compileOutput ?? null;
  const feedback = useMemo(() => submissionCompileFeedback({ status, verdict, languageKey, compileOutput }), [status, verdict, languageKey, compileOutput]);
  const matches = draft !== null && current?.draft === draft && current.hash !== null && submissionId !== null &&
    fingerprints.key === key && fingerprints.values[submissionId] === current.hash && dismissed !== submissionId;
  return { ...feedback, markers: matches ? feedback.errors : [], remember, clear };
}
